import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ASSESSMENT } from '@/constants/testIds';

const AssessmentResult = () => {
  const { id } = useParams();
  const loc = useLocation();
  const initialGenerating = loc.state?.status === 'generating';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(initialGenerating);
  const [translating, setTranslating] = useState(false);
  const [consultFee, setConsultFee] = useState(null);

  useEffect(() => { api.get('/consultations/fee').then(({ data }) => setConsultFee(data.amount)).catch(() => setConsultFee(1000)); }, []);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const fetchOnce = async () => {
      try {
        const { data: d } = await api.get(`/assessment/${id}`);
        if (cancelled) return;
        if (d?.status === 'complete' && d.report) { setData(d); setGenerating(false); setLoading(false); return; }
        if (d?.status === 'failed') { setGenerating(false); setLoading(false); toast.error('Report generation failed.'); return; }
        setGenerating(true); setLoading(false);
        timer = setTimeout(fetchOnce, 2500);
      } catch { timer = setTimeout(fetchOnce, 3000); }
    };
    fetchOnce();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [id]);

  const translate = async (target) => {
    setTranslating(true);
    try {
      const { data: res } = await api.post(`/assessment/${id}/translate?target=${target}`);
      setData((d) => ({ ...(d || {}), report: res.report }));
    } catch { toast.error('Translation failed'); }
    finally { setTranslating(false); }
  };

  if (loading || generating || !data || !data.report) {
    return (
      <div className="bg-sans_bone min-h-[calc(100vh-5rem)]">
        <div className="sans-container max-w-3xl sans-section text-center">
          <div className="mx-auto w-14 h-14 mb-8 relative">
            <div className="absolute inset-0 border-2 border-sans_line rounded-full" />
            <div className="absolute inset-0 border-2 border-transparent border-t-sans_ink rounded-full animate-spin" />
          </div>
          <div className="sans-label mb-6">Analysing your responses</div>
          <h2 className="sans-h2">Preparing your Prakriti…</h2>
          <p className="mt-6 text-sans_mute max-w-md mx-auto leading-relaxed">This usually takes 10–20 seconds.</p>
        </div>
      </div>
    );
  }

  const r = data.report;
  const p = r.percentages || { vata: 0, pitta: 0, kapha: 0 };
  const currentLang = r.lang || 'en';
  // Show a proper 2-paragraph teaser + 2 hidden-symptom hooks. Enough to hook,
  // not enough to replace a consultation.
  const summaryParas = (r.summary || '').split(/\n\n+/).map((s) => s.trim()).filter(Boolean).slice(0, 2);
  const shortSummary = summaryParas.join('\n\n');
  const teaser = (r.hidden_symptoms_probable || []).slice(0, 2);

  return (
    <div className="bg-sans_bone">
      <section className="sans-section">
        <div className="sans-container max-w-3xl">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="sans-label mb-6">{currentLang === 'hi' ? 'आपकी प्रकृति' : 'Your Prakriti'}</div>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-sans_ink tracking-tight leading-none">
                <span className="italic" data-testid={ASSESSMENT.resultPrimary}>{r.primary_dosha}</span>
                {(p[String(r.secondary_dosha || '').toLowerCase()] || 0) > 0 && (
                  <>
                    <span className="text-sans_mute"> – </span>
                    <span className="italic" data-testid={ASSESSMENT.resultSecondary}>{r.secondary_dosha}</span>
                  </>
                )}
              </h1>
              <div className="mt-4 text-sm text-sans_mute tabular-nums">Vata {p.vata}% · Pitta {p.pitta}% · Kapha {p.kapha}%</div>
            </div>
            <div className="inline-flex border border-sans_line">
              <button disabled={translating || currentLang === 'en'} onClick={() => translate('en')} className={`px-4 py-2 text-xs ${currentLang === 'en' ? 'bg-sans_ink text-sans_bone' : 'bg-white text-sans_ink'}`}>English</button>
              <button disabled={translating || currentLang === 'hi'} onClick={() => translate('hi')} className={`px-4 py-2 text-xs border-l border-sans_line ${currentLang === 'hi' ? 'bg-sans_ink text-sans_bone' : 'bg-white text-sans_ink'}`}>हिन्दी</button>
            </div>
          </div>
          <div className="sans-rule mt-12" />
          <div className="mt-12 max-w-2xl space-y-5">
            {shortSummary.split('\n\n').map((para, i) => (
              <p key={i} className="text-lg text-sans_ink leading-relaxed">{para}</p>
            ))}
          </div>

          {teaser.length > 0 && (
            <div className="mt-10 max-w-2xl border-l-2 border-sans_ink pl-6">
              <div className="sans-label mb-3">You may quietly notice</div>
              <ul className="space-y-2 text-sans_ink">
                {teaser.map((t, i) => (
                  <li key={i} className="leading-relaxed">— {t}</li>
                ))}
              </ul>
              <p className="mt-4 text-xs italic text-sans_mute">These often go unspoken. A doctor can trace their root and help you address them.</p>
            </div>
          )}

          <div className="mt-16 bg-sans_ink text-sans_bone p-10 md:p-14">
            <div className="sans-label text-white/60 mb-4">Want to understand your body better?</div>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight">Your Prakriti is one part of understanding your individual constitution. Speak personally with a Saans Ayurvedic Doctor for a more detailed understanding of your health, lifestyle and concerns.</h2>
            <p className="mt-6 text-sm text-sans_bone/70">Ayurvedic Doctor Consultation — {consultFee === 0 ? 'Free' : consultFee != null ? `₹${consultFee.toLocaleString('en-IN')}` : '₹1,000'}</p>
            <Link to="/consultation" className="inline-block mt-10 px-8 py-4 bg-sans_bone text-sans_ink text-sm tracking-wide hover:opacity-90">Book Consultation</Link>
          </div>

          <div className="mt-10 border-t border-sans_line pt-6 text-xs text-sans_mute max-w-2xl">
            A classical Prakriti estimate. Educational and not a medical diagnosis.
          </div>
        </div>
      </section>
    </div>
  );
};

export default AssessmentResult;
